import React, { useContext, useEffect, useState } from 'react';
import { Select, StatusIndicator } from '@amzn/awsui-components-core';
import { BarRaiserStatus, ContentWorkflow } from '@amzn/awscat-pattern-library-service-model';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { getSelectOption, extractUserAlias } from '../../api/UtilsApi';
import { DropdownStatusProps } from '@amzn/awsui-components-core/internal/components/dropdown-status';
import { ContentQueryContext } from '../content/table/ContentQueryContext';
import { WorkflowTableContext } from '../../contexts/WorkflowTableContext';
import { localLog } from '../../loggers/Logger';

interface Props {
  workflow: ContentWorkflow;
  forPublishedContents?: boolean;
}

const BarRaiserAssigneeSelect = ({ workflow, forPublishedContents }: Props) => {
  const {
    contentId,
    draftWorkflowId,
    barRaiserId: draftBarRaiserId,
    publicationWorkflows,
    publicationWorkflowId,
  } = workflow;
  const [isUpdatingBarRaiser, setIsUpdatingBarRaiser] = useState(false);
  const [allOptions, setAllOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const { fetchBarRaisersState } = useContext(WorkflowTableContext);
  const { queryState, dispatchQueryState } = useContext(ContentQueryContext);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);

  const publishedBarRaiserId = publicationWorkflows[0]?.barRaiserId;
  const publishedBarRaiserStatus = publicationWorkflows[0]?.barRaiserStatus;

  const mplServiceClient = MplServiceClientFactory.create();

  // @ts-expect-error TS2339 - strict migration
  const { barRaisers, isLoading, error } = fetchBarRaisersState;

  useEffect(() => {
    const allBarRaisers = barRaisers;
    // @ts-expect-error TS7006 - strict migration
    const allBarRaiserOptions = allBarRaisers.map((barRaiser) => {
      return {
        id: barRaiser.userId,
        label: extractUserAlias(barRaiser.userId),
        value: barRaiser.userId,
      };
    });

    allBarRaiserOptions.push({
      id: 'Empty',
      label: '<Remove assignment>',
      value: '',
    });
    setIsReadOnly(publishedBarRaiserStatus === BarRaiserStatus.UnpublishingRecommended);

    // Each content may have multiple bar raisers: draft vs. published.
    // Which one to render depends on the forPublishedContents property
    const selectedBarRaiserId = forPublishedContents ? publishedBarRaiserId : draftBarRaiserId;

    const selectedBarRaiser = selectedBarRaiserId
      ? getSelectOption(selectedBarRaiserId, extractUserAlias(selectedBarRaiserId))
      : getSelectOption('', '');
    setAllOptions(allBarRaiserOptions);
    // @ts-expect-error TS2345 - strict migration
    setSelectedOption(selectedBarRaiser);
  }, [
    barRaisers,
    contentId,
    draftBarRaiserId,
    forPublishedContents,
    publishedBarRaiserId,
    publishedBarRaiserStatus,
  ]);

  if (fetchBarRaisersState === undefined) {
    localLog.error('Missing context value. BarRaiserAssigneeSelect requires fetchBarRaisersState.');
    return <StatusIndicator type="error">Could not load bar raisers</StatusIndicator>;
  }

  const updateAssignee = async (updatedOption: any) => {
    setSelectedOption(updatedOption);
    setIsUpdatingBarRaiser(true);
    try {
      await mplServiceClient.assignBarRaiser(
        contentId,
        forPublishedContents ? publicationWorkflowId : draftWorkflowId,
        updatedOption.value,
      );
    } finally {
      setIsUpdatingBarRaiser(false);
    }

    // Update the correct bar raiser field in the local query state to match
    // which workflow was actually modified via the API call above.
    // @ts-expect-error TS18048 - strict migration
    const tableItem = queryState.queryResult.find((wf) => wf.contentId === contentId);
    if (forPublishedContents) {
      // @ts-expect-error TS18048 - strict migration
      tableItem.workflow.publicationWorkflows[0].barRaiserId = updatedOption.value;
    } else {
      // @ts-expect-error TS18048 - strict migration
      tableItem.workflow.barRaiserId = updatedOption.value;
    }
    const modifiedQueryResult = [...queryState.queryResult];
    dispatchQueryState({ queryResult: modifiedQueryResult });
  };

  if (!draftWorkflowId) {
    return null;
  }
  if (isUpdatingBarRaiser) {
    return <StatusIndicator type="loading">Updating bar raiser...</StatusIndicator>;
  }

  let statusType: DropdownStatusProps.StatusType = 'finished';
  if (error !== undefined) {
    statusType = 'error';
  }
  if (isLoading) {
    statusType = 'loading';
  }

  return (
    <Select
      selectedOption={selectedOption}
      onChange={({ detail }) => updateAssignee(detail.selectedOption)}
      options={allOptions}
      selectedAriaLabel="Select"
      statusType={statusType}
      loadingText={isLoading ? 'loading bar raisers' : undefined}
      errorText={error}
      expandToViewport
      readOnly={isReadOnly}
      ariaRequired
    />
  );
};

export default BarRaiserAssigneeSelect;
